import React, { useCallback, useEffect, useRef, useState } from "react";
import { PITCH_TABS, STATUS_TONE, tabCount } from "./pitchFields";

/**
 * The six status buckets, as pills with their counters.
 *
 * The ids are FOUNDATION: each one is the `status` query parameter of
 * GET /dashboard/club/tender-pitches/, and `deal` fans out to
 * `deal|signed|external_deal` server-side. The counters are foundation too,
 * including the subtraction fallback for "active" (see `tabCount`) — they are
 * refetched after every action and never counted up in the browser.
 *
 * What is new is the one thing that was measured as broken (inventory 5.3): the
 * strip never scrolled to the selection, so on three of six tabs the active
 * pill sat at 0% visibility at 375px and the club saw a strip with nothing
 * marked at all. The strip now scrolls to the pill it just activated with
 * `scrollTo` — deliberately not `scrollIntoView`, which scrolls the PAGE as
 * well and would yank the list out from under the thumb. A fade edge says the
 * row continues, and it can be dragged with a finger or a mouse.
 */
const ViewPitchesTabs = ({ activeTab, setActiveTab, tender }) => {
  const stripRef = useRef(null);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [showFade, setShowFade] = useState(false);

  const measure = useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    setShowFade(el.scrollWidth > el.clientWidth + 4);
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  const scrollToPill = (el) => {
    const strip = stripRef.current;
    if (!strip || !el) return;
    const target = Math.max(0, el.offsetLeft - 16);
    if (typeof strip.scrollTo === "function") {
      try {
        strip.scrollTo({ left: target, behavior: "smooth" });
        return;
      } catch (err) {
        // Older engines reject the options object; fall through.
      }
    }
    strip.scrollLeft = target;
  };

  const onPointerDown = (event) => {
    const strip = stripRef.current;
    if (!strip) return;
    dragRef.current = { x: event.clientX, left: strip.scrollLeft, moved: 0 };
  };

  const onPointerMove = (event) => {
    const strip = stripRef.current;
    const drag = dragRef.current;
    if (!drag || !strip) return;
    const dx = event.clientX - drag.x;
    if (Math.abs(dx) > 3) {
      drag.moved = Math.max(drag.moved, Math.abs(dx));
      strip.scrollLeft = drag.left - dx;
    }
  };

  const endDrag = () => {
    if (dragRef.current && dragRef.current.moved > 8) {
      suppressClickRef.current = true;
    }
    dragRef.current = null;
  };

  const selectTab = (id, el) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    setActiveTab(id);
    scrollToPill(el);
  };

  return (
    <div className="relative mt-4">
      <div
        ref={stripRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        className="flex flex-nowrap gap-2 overflow-x-auto overflow-y-hidden px-0.5 py-1 [scrollbar-width:none] [touch-action:pan-x] cursor-grab"
      >
        {PITCH_TABS.map((tab) => {
          const tone = STATUS_TONE[tab.id];
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              aria-pressed={isActive}
              onClick={(event) => selectTab(tab.id, event.currentTarget)}
              className={`inline-flex min-h-[44px] flex-none items-center gap-2 whitespace-nowrap rounded-full border px-4 text-[14px] font-semibold ${
                isActive ? tone.pill : "border-[#E5E7EB] bg-white text-[#374151]"
              }`}
            >
              <span
                aria-hidden="true"
                className={`h-2 w-2 flex-shrink-0 rounded-full ${
                  isActive ? tone.activeDot : tone.dot
                }`}
              />
              {tab.label} ({tabCount(tender, tab.id)})
            </button>
          );
        })}
      </div>
      {showFade ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-14 bg-gradient-to-r from-transparent to-[#F5F7FB]"
        />
      ) : null}
    </div>
  );
};

export default ViewPitchesTabs;
